import { useState, useEffect } from 'react';
import { Dialog } from '@printtat/ui';
import { X } from 'lucide-react';
import { Button, TextField, Select } from '@printtat/ui';
import { UserRole } from '@printtat/shared';
import type { AdminUser } from '@/services/adminApi';

const ROLE_OPTIONS = [
  { value: UserRole.USER, label: 'User' },
  { value: UserRole.ADMIN, label: 'Admin' },
  { value: UserRole.PRINTER, label: 'Printer' },
];

const VERIFIED_OPTIONS = [
  { value: 'true', label: 'Verified' },
  { value: 'false', label: 'Not verified' },
];

interface EditUserModalProps {
  user: AdminUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: { firstName?: string; lastName?: string; role?: string; isVerified?: boolean }) => void;
  loading?: boolean;
}

export const EditUserModal = ({ user, open, onOpenChange, onSave, loading }: EditUserModalProps) => {
  const [firstName, setFirstName] = useState(user.firstName ?? '');
  const [lastName, setLastName] = useState(user.lastName ?? '');
  const [role, setRole] = useState(user.role);
  const [verified, setVerified] = useState(String(user.isVerified));

  useEffect(() => {
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
    setRole(user.role);
    setVerified(String(user.isVerified));
  }, [user]);

  const handleSave = () => {
    onSave({
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      role,
      isVerified: verified === 'true',
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-xl">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              Edit user
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X size={18} className="text-gray-400" />
              </button>
            </Dialog.Close>
          </div>

          <p className="text-sm text-gray-500 mb-5">{user.email}</p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
              <TextField
                value={firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                placeholder="First name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
              <TextField
                value={lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                placeholder="Last name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <Select
                options={ROLE_OPTIONS}
                value={role}
                onChange={(e) => setRole(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Verification</label>
              <Select
                options={VERIFIED_OPTIONS}
                value={verified}
                onChange={(e) => setVerified(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Dialog.Close asChild>
              <Button variant="outline" disabled={loading}>Cancel</Button>
            </Dialog.Close>
            <Button onClick={handleSave} loading={loading}>Save changes</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
